import React, { createContext, useContext, useReducer, useCallback } from "react";

// ─── Module-level event bus ──────────────────────────────────────────
// Allows IsLoggedIn.js to route incoming socket messages to open MiniChats
// without prop-drilling through the React tree.
export const miniChatBus = {
  _handlers: new Map(),
  _openChatHandler: null,
  _pendingMessages: new Map(),
  subscribe(chatId, fn) {
    this._handlers.set(chatId, fn);
  },
  unsubscribe(chatId) {
    this._handlers.delete(chatId);
  },
  registerOpenChat(fn) {
    this._openChatHandler = fn;
  },
  unregisterOpenChat(fn) {
    if (!fn || this._openChatHandler === fn) {
      this._openChatHandler = null;
    }
  },
  openChat(user) {
    if (this._openChatHandler && user) {
      this._openChatHandler(user);
    }
  },
  queueMessage(message) {
    const senderId = String(message?.sender?._id || message?.sender?.id || "");
    if (!senderId) return;
    const queued = this._pendingMessages.get(senderId) || [];
    queued.push(message);
    this._pendingMessages.set(senderId, queued);
  },
  drainQueuedMessages(chatId) {
    const id = String(chatId || "");
    if (!id) return [];
    const queued = this._pendingMessages.get(id) || [];
    this._pendingMessages.delete(id);
    return queued;
  },
  /** Check if any open mini chat matches the sender */
  hasChatForSender(senderId) {
    return this._handlers.has(String(senderId));
  },
  /** Broadcast an incoming message to all open mini chats */
  emit(message) {
    for (const [, fn] of this._handlers) {
      fn(message);
    }
  },
};

// ─── Reducer ─────────────────────────────────────────────────────────
const MAX_OPEN_CHATS = 3;

function reducer(state, action) {
  switch (action.type) {
    case "OPEN_CHAT": {
      const { user } = action.payload;
      const id = String(user._id || user.id);
      // If already open, just un-minimize it
      const existing = state.find((c) => c.id === id);
      if (existing) {
        return state.map((c) => (c.id === id ? { ...c, minimized: false } : c));
      }
      // If at max, close the leftmost (oldest) one
      const trimmed = state.length >= MAX_OPEN_CHATS ? state.slice(1) : state;
      return [
        ...trimmed,
        { id, user, conversationId: null, minimized: false },
      ];
    }
    case "CLOSE_CHAT": {
      return state.filter((c) => c.id !== action.payload);
    }
    case "TOGGLE_MINIMIZE": {
      return state.map((c) =>
        c.id === action.payload ? { ...c, minimized: !c.minimized } : c
      );
    }
    case "SET_CONVERSATION_ID": {
      const { chatId, conversationId } = action.payload;
      return state.map((c) =>
        c.id === chatId ? { ...c, conversationId } : c
      );
    }
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────
const MiniChatContext = createContext(null);

export function MiniChatProvider({ user, children }) {
  const [openChats, dispatch] = useReducer(reducer, []);

  const openMiniChat = useCallback(
    (friend) => dispatch({ type: "OPEN_CHAT", payload: { user: friend } }),
    []
  );

  const closeMiniChat = useCallback(
    (chatId) => dispatch({ type: "CLOSE_CHAT", payload: chatId }),
    []
  );

  const toggleMinimize = useCallback(
    (chatId) => dispatch({ type: "TOGGLE_MINIMIZE", payload: chatId }),
    []
  );

  const setConversationId = useCallback(
    (chatId, conversationId) =>
      dispatch({
        type: "SET_CONVERSATION_ID",
        payload: { chatId, conversationId },
      }),
    []
  );

  const value = {
    openChats,
    currentUser: user,
    openMiniChat,
    closeMiniChat,
    toggleMinimize,
    setConversationId,
  };

  React.useEffect(() => {
    miniChatBus.registerOpenChat(openMiniChat);
    return () => miniChatBus.unregisterOpenChat(openMiniChat);
  }, [openMiniChat]);

  return (
    <MiniChatContext.Provider value={value}>
      {children}
    </MiniChatContext.Provider>
  );
}

export function useMiniChat() {
  const ctx = useContext(MiniChatContext);
  if (!ctx) throw new Error("useMiniChat must be used within MiniChatProvider");
  return ctx;
}
